import { gql } from "@apollo/client";
import {
  getPayCashEquivalent,
  getSalaryCashComp,
  isHourlyComp,
} from "@asmbl/shared/compensation";
import { getNewPay } from "@asmbl/shared/employee";
import { FeatureFlag } from "@asmbl/shared/feature-flags";
import { zero } from "@asmbl/shared/money";
import { useCompStructure } from "src/components/CompStructureContext";
import { useFeatureFlags } from "src/components/FeatureContext";
import { CondensedTableNewSalaryCell2_participant as Participant } from "../../../../__generated__/graphql";
import { AssembleTruncatedTypography } from "../../../../components/AssembleTruncatedTypography";
import { useCurrencies } from "../../../../components/CurrenciesContext";
import { getSimpleCashLabel } from "../../../../models/Currency";
import { ColumnComponent2 } from "../CondensedTable/CondensedTableInner";
import { ColumnIds, ColumnIdsToHeaders } from "../Contexts/ColumnOrderContext";
import { LARGE_COL_WIDTH } from "./dimensions";

type Props = {
  row: { original: Participant };
};

export function CondensedTableNewPayCell({
  row: { original: employee },
}: Props): JSX.Element {
  const { compStructure } = useCompStructure();
  const { isEnabled } = useFeatureFlags();
  const showHourly = Boolean(
    isEnabled(FeatureFlag.HourlyEmployeesInCompCycles) &&
      compStructure?.allowHourlyEmployees
  );
  const { currencies, defaultCurrency } = useCurrencies();
  const currSalary = getSalaryCashComp(employee.subject.activeCashCompensation);
  const newPay = getNewPay(
    {
      activeCashCompensation: employee.subject.activeCashCompensation,
      compRecommendation: employee.compRecommendation,
    },
    currencies,
    defaultCurrency
  );

  const isHourly = showHourly && isHourlyComp(newPay?.unitType);
  const newPayValue =
    newPay != null
      ? getPayCashEquivalent(newPay)
      : zero(currSalary?.annualCashEquivalent.currency ?? defaultCurrency.code);

  return (
    <AssembleTruncatedTypography align="right">
      {getSimpleCashLabel(newPayValue, false, isHourly)}
    </AssembleTruncatedTypography>
  );
}

CondensedTableNewPayCell.fragments = {
  participant: gql`
    fragment CondensedTableNewSalaryCell2_participant on CompCycleParticipant {
      subject {
        adjustedCashBands(currencyCode: $currencyCode) {
          id
          name
          bandPoints {
            value {
              ... on CashValue {
                annualRate
                currencyCode
              }
            }
          }
        }
        activeCashCompensation(currencyCode: $currencyCode) {
          employeeId
          type
          activeAt
          annualCashEquivalent
          hourlyCashEquivalent
          unit
        }
      }
      compRecommendation(skipEligibility: $skipEligibility) {
        subjectId
        compCycleId
        latestSubmittedPayIncrease(currencyCode: $currencyCode) {
          annualCashEquivalent
          hourlyCashEquivalent
          unitType
        }
      }
    }
  `,
};

CondensedTableNewPayCell.Header = ColumnIdsToHeaders.get(ColumnIds.NEW_SALARY);
CondensedTableNewPayCell.id = ColumnIds.NEW_SALARY;

const column: ColumnComponent2["column"] = {
  Cell: CondensedTableNewPayCell,
  Header: CondensedTableNewPayCell.Header,
  id: CondensedTableNewPayCell.id,
  width: LARGE_COL_WIDTH,
};
CondensedTableNewPayCell.column = column;
